'use client';

import { useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { ErrorBoundary } from 'react-error-boundary';
import type { MediaSource } from '@/shared/types';
import {
  junkdrawerDocumentApiPathFromUrl,
  parseCitationPageFromMedia,
  toJunkdrawerServiceUrlParam,
} from '@/shared/functions';
import { useJunkdrawerDocumentPage } from '@/hooks/useJunkdrawerDocumentPage';
import { capturePdfCanvasThumbnail } from '@/shared/pdfCanvasThumbnail';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const pdfjsOptions = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

const PDF_CAPTURE_TIMEOUT_MS = 25_000;

export type PdfThumbnailCaptureHostProps = {
  media: MediaSource | null;
  onCaptured: (dataUrl: string | null) => void;
};

/**
 * Hidden react-pdf host that renders one citation page so auto-thumbnail
 * backfill can clone its canvas. Compact table/accordion PDF views never
 * mount a page canvas, so this is the only capture surface for those events.
 */
export function PdfThumbnailCaptureHost({
  media,
  onCaptured,
}: PdfThumbnailCaptureHostProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const capturedForRef = useRef<MediaSource | null>(null);
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;

  const s3Url = media
    ? toJunkdrawerServiceUrlParam(
        media.srcToUse || media.mediaSrc || media.mediaUrl || '',
      )
    : '';
  const page = media ? parseCitationPageFromMedia(media) : 1;
  const apiPath = junkdrawerDocumentApiPathFromUrl(s3Url, 'pdf');

  const { objectUrl, error } = useJunkdrawerDocumentPage({
    s3Url,
    page,
    apiPath,
    enabled: !!media && !!s3Url,
  });

  useEffect(() => {
    capturedForRef.current = null;
  }, [media, objectUrl]);

  useEffect(() => {
    if (!media) return;
    const timeoutId = window.setTimeout(() => {
      if (capturedForRef.current === media) return;
      capturedForRef.current = media;
      onCapturedRef.current(null);
    }, PDF_CAPTURE_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [media, objectUrl]);

  useEffect(() => {
    if (!media || !error) return;
    if (capturedForRef.current === media) return;
    capturedForRef.current = media;
    onCapturedRef.current(null);
  }, [media, error]);

  const finish = (dataUrl: string | null) => {
    if (!media || capturedForRef.current === media) return;
    capturedForRef.current = media;
    onCapturedRef.current(dataUrl);
  };

  if (!media || !s3Url) {
    return null;
  }

  return (
    <div
      aria-hidden
      data-testid="pdf-thumbnail-capture-host"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: 200,
        height: 280,
        opacity: 0.01,
        pointerEvents: 'none',
        zIndex: -1,
        overflow: 'hidden',
      }}
    >
      {objectUrl ? (
        <ErrorBoundary
          FallbackComponent={() => null}
          onError={() => finish(null)}
          resetKeys={[objectUrl]}
        >
          <div ref={containerRef}>
            <Document
              file={objectUrl}
              options={pdfjsOptions}
              onLoadError={() => finish(null)}
              loading={null}
            >
              <Page
                pageNumber={1}
                width={200}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onRenderSuccess={() => {
                  const dataUrl = capturePdfCanvasThumbnail(containerRef.current);
                  finish(dataUrl);
                }}
                onRenderError={() => finish(null)}
              />
            </Document>
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
}
